import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import clsx from "clsx";

const RARITY = {
  common:    { color: "#9ca3af", glow: "rgba(156,163,175,0.35)", bg: "rgba(156,163,175,0.06)", border: "rgba(156,163,175,0.28)", label: "Gewöhnlich" },
  rare:      { color: "#3b82f6", glow: "rgba(59,130,246,0.45)",  bg: "rgba(59,130,246,0.07)",  border: "rgba(59,130,246,0.5)",   label: "Selten"     },
  epic:      { color: "#8b5cf6", glow: "rgba(139,92,246,0.5)",   bg: "rgba(139,92,246,0.08)",  border: "rgba(139,92,246,0.6)",   label: "Episch"     },
  legendary: { color: "#fbbf24", glow: "rgba(251,191,36,0.55)",  bg: "rgba(251,191,36,0.06)",  border: "rgba(251,191,36,0.72)",  label: "Legendär"   },
};

const CONDITION_LABELS = {
  xp:         (v) => `${v.toLocaleString()} XP erreichen`,
  streak:     (v) => `${v} Tage Streak`,
  quiz_count: (v) => `${v} Quizze abschließen`,
  coins:      (v) => `${v} Coins besitzen`,
  task_count: (v) => `${v} Aufgaben erledigen`,
  manual:     ()  => "Wird manuell vergeben",
};

function hint(condition, conditionValue) {
  const fn = CONDITION_LABELS[condition];
  return fn ? fn(conditionValue) : condition;
}

// ── Equipped title display ────────────────────────────────────────────────────
function EquippedBanner({ title, onUnequip, loading }) {
  if (!title) return (
    <div className="p-4 rounded-2xl border border-dashed border-nexus-border text-center"
      style={{ background: "rgba(13,13,43,0.4)" }}>
      <p className="text-sm text-gray-500">Kein Titel ausgerüstet</p>
      <p className="text-[10px] text-gray-600 mt-0.5">Wähle einen Titel aus deiner Sammlung</p>
    </div>
  );

  const meta = RARITY[title.rarity] || RARITY.common;

  return (
    <div className="p-4 rounded-2xl border-2 relative overflow-hidden"
      style={{ borderColor: meta.border, background: meta.bg, boxShadow: `0 0 30px ${meta.glow.replace(/[\d.]+\)$/, "0.15)")}` }}>
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg,transparent,${meta.color}60,transparent)` }}/>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-1">Aktiver Titel</p>
          <p
            className="text-lg font-black"
            style={title.gradient
              ? { background: title.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
              : { color: title.color }}
          >
            {title.name}
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">{title.description}</p>
        </div>
        <button
          onClick={onUnequip}
          disabled={loading}
          className="shrink-0 text-[10px] font-bold px-3 py-1.5 rounded-xl border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-all duration-200 disabled:opacity-50"
        >
          Ablegen
        </button>
      </div>
    </div>
  );
}

// ── Owned title card ──────────────────────────────────────────────────────────
function OwnedTitleCard({ st, onEquip, loading }) {
  const meta = RARITY[st.title.rarity] || RARITY.common;

  return (
    <div
      className={clsx(
        "relative p-4 rounded-2xl border-2 overflow-hidden group transition-all duration-300",
        st.isEquipped ? "scale-[1.01]" : "hover:-translate-y-0.5 hover:scale-[1.01]",
      )}
      style={{
        borderColor: st.isEquipped ? meta.border : "rgba(255,255,255,0.08)",
        background:  st.isEquipped ? meta.bg : "rgba(13,13,43,0.5)",
        boxShadow:   st.isEquipped ? `0 4px 20px ${meta.glow.replace(/[\d.]+\)$/, "0.2)")}` : undefined,
      }}
    >
      {st.isEquipped && (
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg,transparent,${meta.color}55,transparent)` }}/>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[8px] font-black px-1.5 py-0.5 rounded-md tracking-wide"
              style={{ color: meta.color, background: `${meta.color}18`, border: `1px solid ${meta.color}30` }}>
              {meta.label.toUpperCase()}
            </span>
            {st.isEquipped && (
              <span className="text-[8px] font-black text-green-400">● AKTIV</span>
            )}
          </div>
          <p
            className="font-black text-sm truncate"
            style={st.title.gradient
              ? { background: st.title.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
              : { color: st.title.color }}
          >
            {st.title.name}
          </p>
          <p className="text-[10px] text-gray-500 leading-snug mt-0.5">{st.title.description}</p>
          <p className="text-[9px] text-gray-600 mt-1">
            Erhalten: {new Date(st.unlockedAt).toLocaleDateString("de-DE")}
          </p>
        </div>

        {!st.isEquipped && (
          <button
            onClick={() => onEquip(st.title.id)}
            disabled={loading}
            className="shrink-0 text-[10px] font-bold px-3 py-1.5 rounded-xl border border-violet-500/40 text-violet-300 bg-violet-500/15 hover:bg-violet-500/25 transition-all duration-200 disabled:opacity-50"
          >
            Ausrüsten
          </button>
        )}
      </div>
    </div>
  );
}

// ── Locked title card ─────────────────────────────────────────────────────────
function LockedTitleCard({ title }) {
  const meta = RARITY[title.rarity] || RARITY.common;
  return (
    <div className="relative p-4 rounded-2xl border opacity-40 hover:opacity-55 transition-all duration-200"
      style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(13,13,43,0.5)" }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[8px] font-black px-1.5 py-0.5 rounded-md tracking-wide"
            style={{ color: meta.color, background: `${meta.color}18`, border: `1px solid ${meta.color}25` }}>
            {meta.label.toUpperCase()}
          </span>
          <p className="font-black text-sm text-gray-400 mt-1">{title.name}</p>
          <p className="text-[10px] text-gray-600 mt-0.5">{title.description}</p>
          <p className="text-[9px] text-cyan-700 mt-1">{hint(title.condition, title.conditionValue)}</p>
        </div>
        <div className="text-xl opacity-40">🔒</div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Titles() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");

  const { data: allData,  isLoading: la } = useQuery({
    queryKey: ["gamification-titles"],
    queryFn:  () => api.get("/gamification/titles").then((r) => r.data),
  });
  const { data: mineData, isLoading: lm } = useQuery({
    queryKey: ["gamification-titles-mine"],
    queryFn:  () => api.get("/gamification/titles/mine").then((r) => r.data),
  });

  const equip = useMutation({
    mutationFn: (titleId) => api.post(`/gamification/titles/equip/${titleId}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["gamification-titles-mine"] }),
  });
  const unequip = useMutation({
    mutationFn: () => api.post("/gamification/titles/unequip"),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["gamification-titles-mine"] }),
  });

  if (la || lm) return (
    <div className="flex justify-center pt-20">
      <div className="w-10 h-10 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-transparent border-l-transparent animate-spin"/>
    </div>
  );

  const all       = allData?.titles  || [];
  const mine      = mineData?.titles || [];
  const ownedMap  = new Map(mine.map((st) => [st.titleId, st]));
  const equipped  = mine.find((st) => st.isEquipped);
  const locked    = all.filter((t) => !ownedMap.has(t.id));

  const visibleOwned  = filter !== "locked"  ? mine  : [];
  const visibleLocked = filter !== "earned"  ? locked : [];
  const isBusy = equip.isPending || unequip.isPending;

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-2 relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(251,191,36,0.06) 0%, transparent 70%)" }}/>
        <div className="text-5xl mb-3 inline-block animate-float"
          style={{ filter: "drop-shadow(0 0 22px rgba(251,191,36,0.6))" }}>👑</div>
        <h1 className="text-2xl font-black">Titel</h1>
        <p className="text-xs text-gray-500 mt-1">{mine.length} / {all.length} freigeschaltet</p>
      </div>

      {/* ── Equipped banner ─────────────────────────────────────────────────── */}
      <EquippedBanner
        title={equipped?.title || null}
        onUnequip={() => unequip.mutate()}
        loading={isBusy}
      />

      {/* ── Filter ─────────────────────────────────────────────────────────── */}
      {all.length > 0 && (
        <div className="flex gap-1 p-1 rounded-xl border border-nexus-border"
          style={{ background: "rgba(13,13,43,0.6)" }}>
          {[
            { key: "all",    label: "Alle" },
            { key: "earned", label: `Erhalten (${mine.length})` },
            { key: "locked", label: `Gesperrt (${locked.length})` },
          ].map((t) => (
            <button key={t.key} onClick={() => setFilter(t.key)}
              className={clsx(
                "flex-1 py-1.5 rounded-lg text-xs font-black transition-all duration-200",
                filter === t.key ? "bg-violet-600 text-white shadow-lg" : "text-gray-400 hover:text-gray-200"
              )}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────────── */}
      {all.length === 0 && (
        <div className="p-10 rounded-2xl border border-nexus-border text-center"
          style={{ background: "rgba(13,13,43,0.5)" }}>
          <div className="text-5xl mb-3">👑</div>
          <p className="font-black text-lg">Noch keine Titel</p>
          <p className="text-xs text-gray-500 mt-1">Titel werden von Administratoren erstellt.</p>
        </div>
      )}

      {/* ── Owned ───────────────────────────────────────────────────────────── */}
      {visibleOwned.length > 0 && (
        <section>
          <h3 className="font-black text-sm mb-3 flex items-center gap-2">
            <span className="text-green-400">✅</span>
            <span>Freigeschaltet ({mine.length})</span>
          </h3>
          <div className="space-y-2">
            {visibleOwned.map((st) => (
              <OwnedTitleCard key={st.id} st={st} onEquip={(id) => equip.mutate(id)} loading={isBusy} />
            ))}
          </div>
        </section>
      )}

      {/* ── Locked ──────────────────────────────────────────────────────────── */}
      {visibleLocked.length > 0 && (
        <section>
          <h3 className="font-black text-sm mb-3 flex items-center gap-2">
            <span className="text-gray-500">🔒</span>
            <span className="text-gray-400">Noch zu verdienen ({locked.length})</span>
          </h3>
          <div className="space-y-2">
            {visibleLocked.map((t) => <LockedTitleCard key={t.id} title={t} />)}
          </div>
        </section>
      )}

      <div className="pb-2"/>
    </div>
  );
}
